import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const TENETS = [
    { num: "i.", italic: "Clarity", rest: "over complexity.", note: "If a system can't be explained in one breath, it isn't finished." },
    { num: "ii.", italic: "Systems", rest: "designed to scale.", note: "Decisions made today should hold their shape in three years." },
    { num: "iii.", italic: "Technology", rest: "that feels invisible.", note: "The right software disappears behind the work it enables." },
    { num: "iv.", italic: "Small teams.", rest: "Deep focus.", note: "Fewer hands, more attention. Quality is a posture, not an output." },
];

export default function Philosophy() {
    return (
        <section id="philosophy" data-testid="philosophy-section" className="relative py-32 md:py-48 overflow-hidden">
            <div className="mx-auto max-w-[1480px] px-6 sm:px-12 md:px-20">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-15%" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="mb-16 flex items-baseline gap-6 border-b border-sb pb-6"
                >
                    <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-sb-mint">03 · Tenets</span>
                    <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.24em] text-sb-muted">/ Quiet rules</span>
                    <span className="ml-auto hidden md:inline font-mono text-[10px] uppercase tracking-[0.24em] text-sb-muted">Four principles</span>
                </motion.div>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="max-w-[46ch] text-sb-muted text-base md:text-lg font-light leading-relaxed mb-16 md:mb-24"
                >
                    The opinions we hold quietly, and the ones our work tries to honour.
                </motion.p>

                <ul className="flex flex-col">
                    {TENETS.map((t, i) => (
                        <TenetRow key={t.num} t={t} i={i} />
                    ))}
                </ul>
            </div>

            <div className="pointer-events-none absolute right-0 top-1/4 h-[40vmax] w-[40vmax] mint-glow opacity-40" aria-hidden />
        </section>
    );
}

function TenetRow({ t, i }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.95", "start 0.4"] });
    const opacity = useTransform(scrollYProgress, [0, 1], [0.55, 1]);
    const x = useTransform(scrollYProgress, [0, 1], [12, 0]);

    return (
        <li ref={ref} data-testid={`philosophy-statement-${i}`} className="border-t border-sb">
            <motion.div style={{ opacity, x }} className="grid grid-cols-1 md:grid-cols-12 gap-6 py-8 md:py-12">
                <div className="md:col-span-1 flex items-start">
                    <span className="font-mono text-xs tracking-[0.2em] text-sb-mint pt-3">{t.num}</span>
                </div>
                <div className="md:col-span-8">
                    <h3 className="balance text-[clamp(1.75rem,3.6vw,3.25rem)] font-medium leading-[1.05] tracking-[-0.025em]">
                        <span className="font-serif italic">{t.italic}</span>{" "}
                        <span>{t.rest}</span>
                    </h3>
                </div>
                <div className="md:col-span-3 md:col-start-10 flex items-end">
                    <p className="text-sm text-sb-muted font-light leading-relaxed max-w-[28ch] border-l border-sb pl-4">
                        {t.note}
                    </p>
                </div>
            </motion.div>
        </li>
    );
}
